// ============================================================================
// Real Inventory Service - API Integration
// ============================================================================
// Description: Service for real API calls to inventory management endpoints
// Replaces mock service with actual backend integration
// ============================================================================

import apiClient from './apiService';
import { 
  InventoryCategory,
  InventoryType,
  InventoryStatus,
  ProjectInventory,
  InventoryCluster,
  InventoryStatistics
} from '../types/inventory';

// ============================================================================
// API Response Interfaces
// ============================================================================

interface ApiResponse<T> {
  success: boolean;
  data: T;
  count?: number;
  message?: string;
  error?: string;
}

// ============================================================================
// INVENTORY CATEGORIES SERVICE
// ============================================================================

export const categoriesService = {
  // Get all categories
  getAll: async (params?: { is_active?: boolean; search?: string; order_by?: string }): Promise<InventoryCategory[]> => {
    const response = await apiClient.get<ApiResponse<InventoryCategory[]>>('/api/inventory/categories', { params });
    return response.data.data;
  },

  // Get active categories only
  getActive: async (): Promise<InventoryCategory[]> => {
    const response = await apiClient.get<ApiResponse<InventoryCategory[]>>('/api/inventory/categories/active');
    return response.data.data;
  },

  // Get category by ID
  getById: async (id: number): Promise<InventoryCategory> => {
    const response = await apiClient.get<ApiResponse<InventoryCategory>>(`/api/inventory/categories/${id}`);
    return response.data.data;
  },

  // Get category by code
  getByCode: async (code: string): Promise<InventoryCategory> => {
    const response = await apiClient.get<ApiResponse<InventoryCategory>>(`/api/inventory/categories/by-code/${code}`);
    return response.data.data;
  },

  // Create new category
  create: async (category: Omit<InventoryCategory, 'id' | 'created_at' | 'updated_at'>): Promise<InventoryCategory> => {
    const response = await apiClient.post<ApiResponse<InventoryCategory>>('/api/inventory/categories', category);
    return response.data.data;
  },

  // Update category
  update: async (id: number, updates: Partial<InventoryCategory>): Promise<InventoryCategory> => {
    const response = await apiClient.put<ApiResponse<InventoryCategory>>(`/api/inventory/categories/${id}`, updates);
    return response.data.data;
  },

  // Delete category (soft delete)
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/api/inventory/categories/${id}`);
  }
};

// ============================================================================
// INVENTORY TYPES SERVICE
// ============================================================================

export const typesService = {
  // Get all types
  getAll: async (params?: { category_id?: number; is_active?: boolean; search?: string }): Promise<InventoryType[]> => {
    const response = await apiClient.get<ApiResponse<InventoryType[]>>('/api/inventory/types', { params });
    return response.data.data;
  },

  // Get types by category
  getByCategory: async (categoryId: number): Promise<InventoryType[]> => {
    const response = await apiClient.get<ApiResponse<InventoryType[]>>(`/api/inventory/types/by-category/${categoryId}`);
    return response.data.data;
  },

  // Get type by code
  getByCode: async (code: string): Promise<InventoryType> => {
    const response = await apiClient.get<ApiResponse<InventoryType>>(`/api/inventory/types/by-code/${code}`);
    return response.data.data;
  },

  // Get active types only
  getActive: async (): Promise<InventoryType[]> => {
    const response = await apiClient.get<ApiResponse<InventoryType[]>>('/api/inventory/types/active');
    return response.data.data;
  },

  // Get type by ID
  getById: async (id: number): Promise<InventoryType> => {
    const response = await apiClient.get<ApiResponse<InventoryType>>(`/api/inventory/types/${id}`);
    return response.data.data;
  },

  // Create new type
  create: async (type: Omit<InventoryType, 'id' | 'created_at' | 'updated_at'>): Promise<InventoryType> => {
    const response = await apiClient.post<ApiResponse<InventoryType>>('/api/inventory/types', type);
    return response.data.data;
  },

  // Update type
  update: async (id: number, updates: Partial<InventoryType>): Promise<InventoryType> => {
    const response = await apiClient.put<ApiResponse<InventoryType>>(`/api/inventory/types/${id}`, updates);
    return response.data.data;
  },

  // Delete type
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/api/inventory/types/${id}`);
  }
};

// ============================================================================
// PLACEHOLDER SERVICES (TO BE IMPLEMENTED)
// ============================================================================

export const statusesService = {
  getAll: async (): Promise<InventoryStatus[]> => {
    // TODO: Implement when statuses API is ready
    return [];
  },
  
  getActive: async (): Promise<InventoryStatus[]> => {
    return [];
  },

  create: async (status: Omit<InventoryStatus, 'id' | 'created_at' | 'updated_at'>): Promise<InventoryStatus> => {
    throw new Error('Statuses API not implemented yet');
  },

  update: async (id: number, updates: Partial<InventoryStatus>): Promise<InventoryStatus> => {
    throw new Error('Statuses API not implemented yet');
  },

  delete: async (id: number): Promise<void> => {
    throw new Error('Statuses API not implemented yet');
  }
};

// ============================================================================
// PROJECT INVENTORY SERVICE (MAIN INVENTORY TABLE)
// ============================================================================

interface ProjectInventoryListResponse {
  success: boolean;
  data: ProjectInventory[];
  count: number;
  page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
}

interface ProjectInventoryFilters {
  category_id?: number;
  type_id?: number;
  status_id?: number;
  is_active?: boolean;
  is_deprecated?: boolean;
  is_critical?: boolean;
  owned_by_user?: number;
  programming_language?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export const inventoryService = {
  // Get all inventory items with filtering and pagination
  getAll: async (filters?: ProjectInventoryFilters): Promise<ProjectInventoryListResponse> => {
    const response = await apiClient.get<ProjectInventoryListResponse>('/api/inventory/items', { 
      params: filters 
    });
    return response.data;
  },

  // Get inventory items list (just the data array)
  getItems: async (filters?: ProjectInventoryFilters): Promise<ProjectInventory[]> => {
    const response = await inventoryService.getAll(filters);
    return response.data;
  },

  // Get inventory item by ID
  getById: async (id: number): Promise<ProjectInventory> => {
    const response = await apiClient.get<ApiResponse<ProjectInventory>>(`/api/inventory/items/${id}`);
    return response.data.data;
  },

  // Create new inventory item
  create: async (item: Omit<ProjectInventory, 'id' | 'created_at' | 'updated_at' | 'inventory_code' | 'category_name' | 'type_name' | 'status_name' | 'category_code' | 'type_code' | 'status_code'>): Promise<ProjectInventory> => {
    const response = await apiClient.post<ApiResponse<ProjectInventory>>('/api/inventory/items', item);
    return response.data.data;
  },

  // Update inventory item
  update: async (id: number, updates: Partial<ProjectInventory>): Promise<ProjectInventory> => {
    const response = await apiClient.put<ApiResponse<ProjectInventory>>(`/api/inventory/items/${id}`, updates);
    return response.data.data;
  },

  // Delete inventory item (soft delete)
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/api/inventory/items/${id}`);
  },

  // Search inventory items
  search: async (searchTerm: string, filters?: Omit<ProjectInventoryFilters, 'search'>): Promise<ProjectInventory[]> => {
    const response = await inventoryService.getAll({
      ...filters,
      search: searchTerm
    });
    return response.data;
  },

  // Get inventory items by category
  getByCategory: async (categoryId: number): Promise<ProjectInventory[]> => {
    const response = await inventoryService.getAll({ category_id: categoryId });
    return response.data;
  },

  // Get inventory items by type
  getByType: async (typeId: number): Promise<ProjectInventory[]> => {
    const response = await inventoryService.getAll({ type_id: typeId });
    return response.data;
  },

  // Get critical inventory items
  getCritical: async (): Promise<ProjectInventory[]> => {
    const response = await inventoryService.getAll({ is_critical: true });
    return response.data;
  },

  // Get deprecated inventory items
  getDeprecated: async (): Promise<ProjectInventory[]> => {
    const response = await inventoryService.getAll({ is_deprecated: true });
    return response.data;
  },

  // Get inventory items by programming language
  getByProgrammingLanguage: async (language: string): Promise<ProjectInventory[]> => {
    const response = await inventoryService.getAll({ programming_language: language });
    return response.data;
  },

  // Get inventory items by owner
  getByOwner: async (userId: number): Promise<ProjectInventory[]> => {
    const response = await inventoryService.getAll({ owned_by_user: userId });
    return response.data;
  },

  // Get real statistics from actual data
  getStatistics: async (): Promise<InventoryStatistics> => {
    // Get all items and active categories
    const [allItemsResponse, categories] = await Promise.all([
      inventoryService.getAll({ limit: 1000 }), // Get large batch to calculate stats
      categoriesService.getActive()
    ]);

    const items = allItemsResponse.data;

    // Calculate statistics
    const activeItems = items.filter(item => item.is_active);
    const criticalItems = items.filter(item => item.is_critical);
    const deprecatedItems = items.filter(item => item.is_deprecated);

    // Calculate average test coverage
    const itemsWithCoverage = items.filter(item => item.test_coverage_percentage !== null);
    const avgTestCoverage = itemsWithCoverage.length > 0 
      ? itemsWithCoverage.reduce((sum, item) => sum + (item.test_coverage_percentage || 0), 0) / itemsWithCoverage.length
      : 0;

    // Count components by category
    const componentsByCategory = items.reduce((acc, item) => {
      const categoryCode = item.category_code || 'UNKNOWN';
      acc[categoryCode] = (acc[categoryCode] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Count components by status  
    const componentsByStatus = items.reduce((acc, item) => {
      const statusCode = item.status_code || 'UNKNOWN';
      acc[statusCode] = (acc[statusCode] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      total_components: items.length,
      active_components: activeItems.length,
      critical_components: criticalItems.length,
      deprecated_components: deprecatedItems.length,
      components_by_category: componentsByCategory,
      components_by_status: componentsByStatus,
      avg_test_coverage: Math.round(avgTestCoverage * 10) / 10, // Round to 1 decimal
      total_clusters: 0, // TODO: Implement when clusters API is ready
      avg_cluster_completion: 0
    };
  }
};

export const clustersService = {
  getAll: async (): Promise<InventoryCluster[]> => {
    return [];
  }
};

// ============================================================================
// COMBINED INVENTORY SERVICE (Main Export)
// ============================================================================

const realInventoryService = {
  // Categories
  getCategories: categoriesService.getAll,
  getActiveCategories: categoriesService.getActive,
  createCategory: categoriesService.create,
  updateCategory: categoriesService.update,
  deleteCategory: categoriesService.delete,

  // Types  
  getTypes: typesService.getAll,
  getActiveTypes: typesService.getActive,
  getTypesByCategory: typesService.getByCategory,
  createType: typesService.create,
  updateType: typesService.update,
  deleteType: typesService.delete,

  // Statuses (placeholder)
  getStatuses: statusesService.getAll,
  getActiveStatuses: statusesService.getActive,
  createStatus: statusesService.create,
  updateStatus: statusesService.update,
  deleteStatus: statusesService.delete,

  // Project Inventory Items (Main Table)
  getInventoryItems: inventoryService.getItems,
  getAllInventoryItems: inventoryService.getAll,
  getInventoryItemById: inventoryService.getById,
  createInventoryItem: inventoryService.create,
  updateInventoryItem: inventoryService.update,
  deleteInventoryItem: inventoryService.delete,
  searchInventoryItems: inventoryService.search,
  getInventoryItemsByCategory: inventoryService.getByCategory,
  getInventoryItemsByType: inventoryService.getByType,
  getCriticalInventoryItems: inventoryService.getCritical,
  getDeprecatedInventoryItems: inventoryService.getDeprecated,
  getInventoryItemsByLanguage: inventoryService.getByProgrammingLanguage,
  getInventoryItemsByOwner: inventoryService.getByOwner,

  // Clusters (placeholder)
  getClusters: clustersService.getAll,

  // Statistics
  getStatistics: inventoryService.getStatistics
};

export default realInventoryService;